import * as React from "react";
import { Box, Button, LinearProgress, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * Inside a league card on My Leagues: how many of the league's upcoming games this person is signed up for, and, when
 * there are more they could join, a clear nudge with a button to the league's sign-up page.
 *
 *  - More games open: a tonal highlight with the number up front, a progress bar and a filled button. The brightest
 *    thing on the card, because it is the one thing worth acting on.
 *  - Signed up for everything: a quiet confirmation, nothing to do.
 *  - Games left but they're full: a quiet note, so a nudge never leads to a dead end.
 *
 * Shown to everyone in the league, including organizers and people who haven't booked a game yet: if there are games
 * they could play, they should see them. Shows nothing for a league with no upcoming games or an archived league.
 */
export default function LeagueGameProgress({ league, onSignUp }) {
  const theme = useTheme();
  const upcoming = league.upcomingGameCount ?? 0;
  const mine = league.myUpcomingRegistrationCount ?? 0;
  const open = league.openGameCount ?? 0;

  if (upcoming === 0 || league.isArchived) return null;

  // "More" only makes sense for someone who is already in at least one game.
  const more = mine > 0 ? "more " : "";

  const summary = `You're signed up for ${mine} of ${plural(upcoming, "upcoming game", "upcoming games")}`;
  const percent = Math.min(100, Math.round((mine / upcoming) * 100));

  if (open === 0) {
    const allIn = mine >= upcoming;
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2, color: "text.secondary" }}>
        {allIn ? <CheckCircleOutlineIcon fontSize="small" color="success" aria-hidden /> : null}
        <Typography variant="body2">
          {allIn ? `You're signed up for all ${plural(upcoming, "upcoming game", "upcoming games")}.` : `${summary}. ${mine > 0 ? "The rest are full." : "They are all full."}`}
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      role="group"
      aria-label={`${plural(open, `${more}game`, `${more}games`)} open in ${league.name}`}
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 2,
        // A tonal container in the primary color, with a bold edge, so it reads as the thing to act on.
        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.08),
        borderLeft: 4,
        borderColor: "primary.main",
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <EventAvailableIcon color="primary" aria-hidden />
            <Typography variant="subtitle1" component="p" sx={{ fontWeight: 700, color: "primary.main" }}>
              {plural(open, `${more}game`, `${more}games`)} open for you
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {summary}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={percent}
            aria-label={summary}
            sx={{ mt: 1.5, height: 8, borderRadius: 4, bgcolor: alpha(theme.palette.primary.main, 0.16) }}
          />
        </Box>
        <Button
          variant="contained"
          color="primary"
          endIcon={<ArrowForwardIcon />}
          onClick={onSignUp}
          sx={{ flexShrink: 0, alignSelf: { xs: "stretch", sm: "center" } }}
        >
          {mine > 0 ? "View & sign up for more" : "View & sign up"}
        </Button>
      </Stack>
    </Box>
  );
}
